import {
    AfterViewInit,
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    signal,
    TemplateRef,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
    ApiGetAllResponse,
    ApiLicense,
    ApiTimezone,
    ApiUser,
    ExportService,
    HostsService,
    LicensesService,
    TimezoneService,
    UsersService,
} from '@core';
import { ApiHostLocation } from '@core/services/hosts/hosts.interfaces';
import { LicenseQueryParams } from '@core/services/licenses/licenses.interfaces';
import { LegendItemConfig, MONTH_OPTIONS } from '@features/dashboard/constants/dashboard.constants';
import { SubNavbarService } from '@layouts/main-layout/components/sub-navbar/sub-navbar.service';
import {
    Button,
    Card,
    Dropdown,
    DropdownOption,
    GraphComponent,
    GraphConfig,
    GraphVariant,
    Skeleton,
} from '@ntv360/component-pantry';

import {
    ExternalFilterChip,
    NtvTableCellDirective,
    StatsCardComponent,
    TableComponent,
    TableToolbarButton,
    TableToolbarComponent,
} from '@shared';
import { SVG_ICONS, TIMEZONE } from '@shared/constants';
import { FilterOperator } from '@shared/interfaces/filter-operator.interface';
import {
    addDays,
    buildSharedGraphConfig,
    endOfDay,
    fetchAllBatch,
    formatUtcMonth,
    generateYearOptions,
    getExportData,
    getOldestYearFromData,
    getStandardToolbarButtons,
    Logger,
    mapTimezonesForDropdown,
    startOfWeek,
    toUtcIso,
    truncateText,
    withLoading,
} from '@shared/utils';
import { catchError, EMPTY, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { HOSTS_COLUMNS, HOSTS_STATS } from './constants';
import {
    HostChartData,
    HostsAllResponse,
    HostsAllResponses,
    TopHostEntry,
    UiHostTable,
} from './interface';

/**
 * Component for managing and displaying the list of hosts.
 * Handles host statistics, table display, filtering, analytics chart,
 * and navigation to host details.
 *
 * @example
 * <app-hosts></app-hosts>
 */
@Component({
    selector: 'app-hosts',
    imports: [
        StatsCardComponent,
        TableToolbarComponent,
        Button,
        Dropdown,
        TableComponent,
        GraphComponent,
        Card,
        Skeleton,
        NtvTableCellDirective,
    ],
    templateUrl: './hosts.component.html',
    styleUrl: './hosts.component.scss',
})
export class HostsComponent implements OnInit, AfterViewInit {
    /**
     * Inject sub-navbar service to programmatically project content into its view.
     */
    private subNavbarService = inject(SubNavbarService);

    /**
     * Injected export service for CSV export.
     */
    private exportService = inject(ExportService);

    /**
     * Injected hosts service for API operations.
     */
    private hostsService = inject(HostsService);

    /**
     * Injected licenses service for fetching license data used in analytics.
     */
    private licensesService = inject(LicensesService);

    /**
     * Inject router for navigation.
     */
    private router = inject(Router);

    /**
     * Injected timezone service for fetching timezones.
     */
    private timezoneService = inject(TimezoneService);

    /**
     * Injected users service for fetching user data.
     */
    private usersService = inject(UsersService);

    /**
     * Inject destroy ref to destroy subscriptions and trigger cancellation callbacks.
     */
    private destroyRef = inject(DestroyRef);

    /**
     * Content reference to project into the sub-navbar component.
     */
    protected subNavBarContent = viewChild<TemplateRef<void>>('subNavBarContent');

    /**
     * Signal to track whether the analytics panel is visible.
     */
    protected showAnalytics = signal<boolean>(false);

    /**
     * Guards against re-fetching analytics data on subsequent toggles.
     */
    /** Tracks if the analytics have been lazily loaded. */
    protected analyticsLoaded = signal<boolean>(false);

    /**
     * Signal to track whether to show the switch host modal.
     */
    protected showSwitchModal = signal<boolean>(false);

    /**
     * Signal to track the 'Don't show this message again' checkbox state.
     */
    protected dontShowAgain = signal<boolean>(false);

    /**
     * Loading state for the host analytics chart.
     */
    protected isChartLoading = signal<boolean>(false);

    /**
     * Loading state for the top hosts list.
     */
    protected isTopHostsLoading = signal<boolean>(false);

    /**
     * Signal to track the total number of hosts for the statistics card.
     */
    protected totalHosts = signal<number>(0);

    /**
     * Signal to track hosts created this week for the statistics card.
     */
    protected hostsThisWeek = signal<number>(0);

    /**
     * Signal to track hosts created last week for the statistics card.
     */
    protected hostsLastWeek = signal<number>(0);

    /**
     * Dynamic top hosts list fetched from the backend.
     * Ranked by total license count descending.
     */
    protected topHostsData = signal<TopHostEntry[]>([]);

    /** Current page for hosts list pagination. */
    protected page = signal<number>(1);

    /** Number of items per page for hosts list. */
    protected limit = signal<number>(10);

    /** Total count of hosts available on the server. */
    protected totalCount = signal<number>(0);

    /** Current search term for filtering hosts. */
    protected searchTerm = signal<string>('');

    /** Signal tracking the selected year for filtered data. */
    public readonly selectedYear = signal<number | null>(null);

    /** Signal tracking the selected month index (0 = Jan … 11 = Dec). */
    public readonly selectedMonth = signal<number | null>(null);

    /** The oldest year for which data exists — drives the Year dropdown range. */
    public readonly oldestDataYear = signal<number>(new Date().getFullYear() - 4);

    /** All 12 calendar months for the Month dropdown. */
    public readonly MONTH_OPTIONS = MONTH_OPTIONS;

    /**
     * List of legend items for the chart.
     */
    public readonly legendItems: LegendItemConfig[] = [
        { label: 'Total Host', modifier: 'total' },
        { label: 'New Host', modifier: 'new' },
    ];

    /**
     * Dynamically built Year options: from `oldestDataYear` signal up to
     * the current calendar year, newest first.
     */
    public readonly yearOptions = computed(() => generateYearOptions(this.oldestDataYear()));

    /**
     * Reference to navbar icon constants.
     */
    public icons = SVG_ICONS;

    /**
     * Current year — displayed in the chart subtitle.
     */
    public readonly currentYear = new Date().getFullYear();

    /**
     * Path to the SVG sprite file.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * Table columns definition for the hosts data table.
     */
    public hostsColumns = HOSTS_COLUMNS;

    /**
     * Reactive signal containing the mapped host data displayed in the table.
     */
    public hostsData = signal<UiHostTable[]>([]);

    /**
     * Signal to track the global loading state for the component and its child table.
     */
    public isLoading = signal<boolean>(true);

    /**
     * Signal to track rows currently selected by the user in the table.
     */
    public selectedRows = signal<UiHostTable[]>([]);

    /**
     * Configuration for the graph filter dropdowns.
     */
    public readonly graphFilterConfigs = computed(() => [
        {
            placeholder: 'Month',
            options: this.MONTH_OPTIONS,
            handler: (opt: DropdownOption | null) => this.onMonthChange(opt),
        },
        {
            placeholder: 'Year',
            options: this.yearOptions(),
            handler: (opt: DropdownOption | null) => this.onYearChange(opt),
        },
    ]);

    /**
     * Computed lookup map of hostLocationId → hostLocationName,
     * derived from the already-loaded hostsData signal.
     * Used in loadTopHosts() to resolve human-readable names.
     */
    private readonly hostNameMap = computed(
        () => new Map(this.hostsData().map((h) => [h.id, h.hostName])),
    );

    /**
     * Computed signal for the list of statistics cards to display at the top of the page.
     * Derived from the HOSTS_STATS constant and populated with real-time data.
     *
     * @returns {StatsData[]} Array of statistics card configurations.
     */
    public statsData = computed(() => {
        const stats = JSON.parse(JSON.stringify(HOSTS_STATS));

        stats[0].total = this.totalCount().toString();
        stats[1].total = this.hostsThisWeek().toString();
        stats[2].total = this.hostsLastWeek().toString();

        return stats;
    });

    /**
     * Dynamic chart data fetched from the backend.
     */
    private chartData = signal<HostChartData>({
        categories: [],
        monthly: [],
        cumulative: [],
    });

    /**
     * Creates a fallback empty response that satisfies the ApiGetAllResponse interface.
     * Used for graceful degradation in catchError handlers.
     *
     * @private
     * @returns {ApiGetAllResponse<T>} An empty response object.
     */
    private createEmptyResponse<T>(): ApiGetAllResponse<T> {
        return {
            data: [],
            meta: {
                timestamp: new Date().toISOString(),
                statusCode: 200,
                pagination: {
                    totalCount: 0,
                    page: 1,
                    limit: 10,
                    totalPages: 0,
                    hasNextPage: false,
                    hasPreviousPage: false,
                },
            },
        };
    }
    /**
     * Computed GraphConfig for the host analytics ntv-graph component.
     * Rebuilds whenever chartData changes.
     */
    public readonly hostGraphConfig = computed<GraphConfig>(() => {
        const data = this.chartData();
        return {
            ...buildSharedGraphConfig({
                data: {
                    cumulative: data.cumulative,
                    monthly: data.monthly,
                    categories: data.categories,
                },
                seriesNames: ['Total Host', 'New Host'],
                variant: GraphVariant.LineWithFilterLegend,
                padding: { left: 0, right: 0 },
            }),
            header: 'Host Analytics',
            subHeader: 'Weekly performance overview',
            size: 'large',
            headerMetrics: [{ label: 'Active', value: this.totalHosts() }],
        };
    });

    /**
     * Initializes the component by fetching all hosts and setting up destruction logic.
     *
     * @returns {void}
     */
    public ngOnInit(): void {
        this.getAllHosts();
        this.loadHostStats();

        this.destroyRef.onDestroy(() => {
            this.subNavbarService.clearContent();
        });
    }

    /**
     * Projects the local action template into the shared sub-navbar component.
     * Runs after the view initializes and the TemplateRef is available.
     *
     * @returns {void}
     */
    public ngAfterViewInit(): void {
        if (this.subNavBarContent()) {
            Promise.resolve().then(() => {
                this.subNavbarService.setContent(this.subNavBarContent()!);
            });
        }
    }

    private timezoneOptions = signal<{ label: string; value: number }[]>([]);

    protected selectedTimezone = signal<DropdownOption | null>(null);

    public timezoneConfig = computed(() => ({
        options: this.timezoneOptions(),
        placeholder: 'Timezone',
        fullWidth: false,
        value: this.selectedTimezone()?.value ?? null,
    }));

    public timezoneChips = computed<ExternalFilterChip[]>(() => {
        const opt = this.selectedTimezone();
        if (!opt) return [];
        return [
            {
                label: opt.label,
                groupLabel: 'Timezone',
                onRemove: () => this.selectedTimezone.set(null),
            },
        ];
    });

    /**
     * Computed list of toolbar buttons that reacts to changes in row selection.
     * Adjusts button variants (e.g., danger for bulk delete) based on the number of selected items.
     *
     * @returns {TableToolbarButton[]} Array of toolbar button configurations.
     */
    public toolbarButtons = computed<TableToolbarButton[]>(() =>
        getStandardToolbarButtons(this.selectedRows().length),
    );

    /**
     * Toggles the analytics panel visibility.
     * On the first open, lazily fetches analytics data from the backend.
     *
     * @returns {void}
     */
    public toggleAnalytics(): void {
        this.showAnalytics.update((v) => !v);

        if (this.showAnalytics() && !this.analyticsLoaded()) {
            this.analyticsLoaded.set(true);
            this.isChartLoading.set(true);
            this.isTopHostsLoading.set(true);

            this.loadOldestYear();
            this.loadAnalyticsChart();
            this.loadTopHosts();
        }
    }

    /**
     * Handles month selection from the Month dropdown.
     * Passing `null` resets to full-year view.
     *
     * @param {DropdownOption | null} option - The selected month option.
     * @returns {void}
     */
    public onMonthChange(option: DropdownOption | null): void {
        const value = option !== null ? (option.value as number) : null;
        if (value === this.selectedMonth()) return;
        this.selectedMonth.set(value);
        this.loadAnalyticsChart();
    }

    /**
     * Updates the year filter for the growth chart.
     *
     * @param {DropdownOption | null} option - The selected year option.
     * @returns {void}
     */
    public onYearChange(option: DropdownOption | null): void {
        const value = Number(option?.value);
        if (!value || value === this.selectedYear()) return;
        this.selectedYear.set(value);
        this.loadAnalyticsChart();
    }

    /**
     * Handles filter changes emitted by the ntv-graph built-in filter controls.
     * Maps the graph's string-based month/year to the component's numeric signals.
     */
    public onGraphFilterChange(event: { timeFrame?: string; month?: string; year?: string }): void {
        const monthKeys = [
            'jan',
            'feb',
            'mar',
            'apr',
            'may',
            'jun',
            'jul',
            'aug',
            'sep',
            'oct',
            'nov',
            'dec',
        ];
        const monthIndex = event.month ? monthKeys.indexOf(event.month) : -1;
        const newMonth = monthIndex >= 0 ? monthIndex : null;
        const newYear = Number(event.year);

        const monthChanged = newMonth !== this.selectedMonth();
        const yearChanged = newYear && newYear !== this.selectedYear();

        if (monthChanged) this.selectedMonth.set(newMonth);
        if (yearChanged) this.selectedYear.set(newYear);
        if (monthChanged || yearChanged) this.loadAnalyticsChart();
    }

    public onTimezoneChange(option: DropdownOption | null): void {
        this.selectedTimezone.set(option);
    }

    /**
     * Handles the 'Add Host Place' action, checking for existing confirmation preference.
     *
     * @returns {void}
     */
    public onAddHostPlace(): void {
        alert('This will be redirected to hosts revamp');
    }

    /**
     * Handles search input value changes and triggers a refetch from page 1.
     *
     *
     * @param {string} term - The search string from the search input.
     * @returns {void}
     */
    public onSearch(term: string): void {
        this.searchTerm.set(term);
        this.page.set(1);
        this.getAllHosts();
    }

    /**
     * Handles the "Show More" request from the table by incrementing the page.
     *
     *
     * @returns {void}
     */
    public onShowMore(): void {
        if (this.hostsData().length < this.totalCount()) {
            this.page.update((p) => p + 1);
            this.getAllHosts(true);
        }
    }

    /**
     * Handles the 'Continue' action from the switch host modal.
     * Redirects to the dashboard and optionally saves the skip preference.
     *
     * @returns {void}
     */
    protected onModalContinue(): void {
        if (this.dontShowAgain()) {
            localStorage.setItem('skipHostSwitchConfirmation', 'true');
        }
        this.showSwitchModal.set(false);
        this.router.navigate(['/dashboard']);
    }

    /**
     * Handles the 'Stay on Current' action from the switch host modal.
     * Closes the confirmation dialog without taking any further action.
     *
     * @returns {void}
     */
    protected onModalStay(): void {
        this.showSwitchModal.set(false);
    }

    /**
     * Handles global toolbar actions like exporting the entire table or selected rows to Excel.
     *
     * @param {string} action - The name of the action to perform (e.g., 'Export').
     * @returns {void}
     *
     * @example
     * this.onToolbarAction('Export');
     */
    public onToolbarAction(action: string): void {
        if (action === 'Export') {
            const dataToExport = getExportData(this.selectedRows(), this.hostsData());
            this.exportService.exportToExcel(dataToExport, 'Hosts', this.hostsColumns);
        }
    }

    /**
     * Handles row-level action clicks for editing or deleting specific hosts.
     *
     * @param {Object} event - The action event payload.
     * @param {string} event.action - The name of the action (e.g., 'Edit').
     * @param {UiHostTable} event.row - The mapped data object for the target row.
     * @returns {void}
     */
    public onActionClick(event: { action: string; row: UiHostTable }): void {
        console.log('Action clicked:', event.action, 'Row data:', event.row);
    }

    /**
     * Handles user clicks on specific table cells.
     * Redirects to the dealer profile if the 'dealerName' is clicked, or to host details otherwise.
     *
     * @param {Object} event - Detailed cell click event information.
     * @param {string} event.field - The property name of the clicked cell.
     * @param {UiHostTable[keyof UiHostTable]} event.value - The raw value inside the cell.
     * @param {UiHostTable} event.row - The full data object for the clicked row.
     * @returns {void}
     */
    public onCellClick(event: {
        field: string;
        value: UiHostTable[keyof UiHostTable];
        row: UiHostTable;
    }): void {
        console.log('Cell clicked:', event.field, 'Value:', event.value, 'Row data:', event.row);

        if (event.field === 'dealerName' && event.row.dealerId) {
            const url = `/dealers/${event.row.dealerId}`;
            window.open(url, '_blank');
            return;
        }

        if (event.row.id) {
            const url = `/hosts/${event.row.id}`;
            window.open(url, '_blank');
        }
    }

    /**
     * Updates the reactive selection signal whenever the user checks/unchecks table rows.
     *
     * @param {UiHostTable[]} selectedRows - Array of currently selected mapped host rows.
     * @returns {void}
     */
    public onSelectionChange(selectedRows: UiHostTable[]): void {
        console.log(selectedRows, 'selected rows');
        this.selectedRows.set(selectedRows);
    }

    /**
     * Fetches original host records, timezones, and license data from the API in parallel.
     * Orchestrates a secondary fetch for user details to resolve owner names after IDs are known.
     * Updates the `hostsData` signal with fully resolved and mapped information for the table.
     *
     * @param {boolean} [isLoadMore=false] - If true, appends new data to existing hostsData instead of replacing.
     * @returns {void}
     */
    public getAllHosts(isLoadMore = false): void {
        const now = new Date();
        const startOfThisWeek = startOfWeek(now);
        const startOfLastWeek = addDays(startOfThisWeek, -7);
        const endOfLastWeek = addDays(startOfThisWeek, -1);

        const requests: Record<string, Observable<HostsAllResponses>> = {
            hosts: this.hostsService
                .getAll({
                    page: this.page(),
                    limit: this.limit(),
                    search: this.searchTerm(),
                    fields: [
                        'hostLocationId',
                        'hostLocationName',
                        'ownerId',
                        'addressLine1',
                        'city',
                        'state',
                        'zip',
                        'timezoneId',
                    ],
                })
                .pipe(catchError(() => of(this.createEmptyResponse<ApiHostLocation>()))),
            timezones: this.timezoneService
                .getAll()
                .pipe(catchError(() => of(this.createEmptyResponse<ApiTimezone>()))),
        };

        // On first load, also fetch statistics using limit: 1 efficiency trick
        if (!isLoadMore) {
            requests['totalHosts'] = this.hostsService
                .getAll({ limit: 1 })
                .pipe(catchError(() => of(this.createEmptyResponse<ApiHostLocation>())));
            requests['hostsThisWeek'] = this.hostsService
                .getAll({
                    limit: 1,
                    filters: { createdAt: { _gte: startOfThisWeek.toISOString() } },
                })
                .pipe(catchError(() => of(this.createEmptyResponse<ApiHostLocation>())));
            requests['hostsLastWeek'] = this.hostsService
                .getAll({
                    limit: 1,
                    filters: {
                        createdAt: {
                            _gte: startOfLastWeek.toISOString(),
                            _lte: endOfDay(endOfLastWeek).toISOString(),
                        },
                    },
                })
                .pipe(catchError(() => of(this.createEmptyResponse<ApiHostLocation>())));
        }

        /**
         * Execute all primary host-related requests (hosts, timezones, users, stats) in parallel.
         * Then chains a secondary fetch for licenses to resolve aggregate counts.
         */
        forkJoin(requests)
            .pipe(
                withLoading(this.isLoading),
                catchError((error: unknown) => {
                    Logger.error('HostsComponent', 'Error fetching hosts:', error);
                    return EMPTY;
                }),
                switchMap((response) => {
                    const typedResponse = response as Omit<HostsAllResponse, 'licenses' | 'users'>;

                    const hostIds = typedResponse.hosts.data
                        .map((h: ApiHostLocation) => h.hostLocationId)
                        .filter((id: string | undefined): id is string => !!id);

                    // Only fetch the owner IDs present on this page — 1 call instead of N batches
                    const ownerIds = [
                        ...new Set(
                            typedResponse.hosts.data
                                .map((h: ApiHostLocation) => h.ownerId)
                                .filter((id): id is string => !!id),
                        ),
                    ];

                    const users$ =
                        ownerIds.length > 0
                            ? this.usersService
                                  .getAll({
                                      fields: ['userId', 'firstName', 'lastName'],
                                      filters: { userId: { _in: ownerIds } },
                                      pageSize: ownerIds.length + 5,
                                  })
                                  .pipe(
                                      map((res) => res.data),
                                      catchError(() => of([] as ApiUser[])),
                                  )
                            : of([] as ApiUser[]);

                    const licenses$ =
                        hostIds.length > 0
                            ? fetchAllBatch<ApiLicense, LicenseQueryParams>(
                                  (p: LicenseQueryParams) => this.licensesService.getAll(p),
                                  {
                                      fields: ['hostLocationId'],
                                      filters: { hostLocationId: { _in: hostIds } },
                                  },
                                  { maxRecords: 10000 },
                              ).pipe(catchError(() => of([] as ApiLicense[])))
                            : of([] as ApiLicense[]);

                    return forkJoin({ users: users$, licenses: licenses$ }).pipe(
                        map(({ users, licenses }) => ({
                            ...typedResponse,
                            users,
                            licenses,
                        })),
                    );
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (response) => {
                    const typedResponse = response as HostsAllResponse;
                    // Update stats if available
                    if (typedResponse.totalHosts) {
                        this.totalHosts.set(typedResponse.totalHosts.meta.pagination.totalCount);
                    }
                    if (typedResponse.hostsThisWeek) {
                        this.hostsThisWeek.set(
                            typedResponse.hostsThisWeek.meta.pagination.totalCount,
                        );
                    }
                    if (typedResponse.hostsLastWeek) {
                        this.hostsLastWeek.set(
                            typedResponse.hostsLastWeek.meta.pagination.totalCount,
                        );
                    }

                    // Update timezone dropdown options
                    this.timezoneOptions.set(mapTimezonesForDropdown(typedResponse.timezones.data));

                    // Build maps for mapping
                    const timezoneMap = new Map<number, string>(
                        typedResponse.timezones.data.map((tz: ApiTimezone) => [
                            tz.timezoneId,
                            tz.timezoneName,
                        ]),
                    );
                    const userMap = new Map<string, string>(
                        typedResponse.users.map((user: ApiUser) => [
                            user.userId,
                            `${user.firstName} ${user.lastName}`,
                        ]),
                    );
                    const licenseCountMap = new Map<string, number>();

                    typedResponse.licenses.forEach((lic: ApiLicense) => {
                        if (lic.hostLocationId) {
                            licenseCountMap.set(
                                lic.hostLocationId,
                                (licenseCountMap.get(lic.hostLocationId) || 0) + 1,
                            );
                        }
                    });

                    const newData = this.mappedHostsData(
                        typedResponse.hosts.data,
                        userMap,
                        timezoneMap,
                        licenseCountMap,
                    );

                    if (isLoadMore) {
                        this.hostsData.update((current) => [...current, ...newData]);
                    } else {
                        this.hostsData.set(newData);
                    }

                    this.totalCount.set(typedResponse.hosts.meta.pagination.totalCount);
                },
            });
    }

    /**
     * Fetches per-month host creation counts for the current year and builds
     * both the monthly-new and cumulative-total series for the analytics chart.
     *
     * @private
     * @returns {void}
     */
    private loadAnalyticsChart(): void {
        this.isChartLoading.set(true);
        const currentYear = new Date().getFullYear();
        const year = this.selectedYear() ?? currentYear;
        const month = this.selectedMonth();
        const months = month !== null ? [month] : Array.from({ length: 12 }, (_, i) => i);

        forkJoin(
            months.map((monthIndex) =>
                this.hostsService
                    .getAll({
                        limit: 1,
                        sortBy: 'createdAt',
                        sortOrder: 'desc',
                        filters: this.buildDateFilters(
                            toUtcIso(year, monthIndex, 1),
                            toUtcIso(year, monthIndex + 1, 0, 23, 59, 59, 999),
                        ),
                    })
                    .pipe(map((res) => res.meta.pagination.totalCount)),
            ),
        )
            .pipe(
                withLoading(this.isChartLoading),
                catchError((error: unknown) => {
                    Logger.error('HostsComponent', 'Error loading analytics chart:', error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (monthly) => {
                    let running = 0;
                    const cumulative = monthly.map((count) => {
                        running += count;
                        return running;
                    });
                    this.chartData.set({
                        categories: months.map((m) => formatUtcMonth(year, m)),
                        monthly,
                        cumulative,
                    });
                },
            });
    }

    /**
     * Fetches host statistics from the dedicated stats endpoint.
     * For now, just logs the results.
     */
    private loadHostStats(): void {
        this.hostsService
            .getStats()
            .pipe(
                catchError((error: unknown) => {
                    Logger.error('HostsComponent', 'Error loading host stats:', error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (stats) => {
                    console.log('Host stats:', stats);
                },
            });
    }

    /**
     * Fetches the oldest year with data for the active dataset.
     * Used to populate the year dropdown range.
     *
     * @private
     * @returns {void}
     */
    private loadOldestYear(): void {
        this.hostsService
            .getAll({
                limit: 1,
                sortBy: 'createdAt',
                sortOrder: 'asc',
            })
            .pipe(
                map((response) => getOldestYearFromData(response.data)),
                catchError((error: unknown) => {
                    Logger.error('HostsComponent', 'Error loading oldest data year:', error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (year) => this.oldestDataYear.set(year),
            });
    }

    /**
     * Fetches license records grouped by host location and produces the
     * Top 10 hosts ranked by total license count.
     *
     * @private
     * @returns {void}
     */
    private loadTopHosts(): void {
        this.isTopHostsLoading.set(true);
        fetchAllBatch<ApiLicense, LicenseQueryParams>(
            (p: LicenseQueryParams) => this.licensesService.getAll(p),
            { fields: ['hostLocationId'] },
            { maxRecords: 10000 },
        )
            .pipe(
                switchMap((allLicenses) => {
                    const counts = new Map<string, number>();
                    allLicenses.forEach((lic) => {
                        const id = lic.hostLocationId;
                        if (!id) return;
                        counts.set(id, (counts.get(id) ?? 0) + 1);
                    });

                    const top10 = Array.from(counts.entries())
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 10);

                    const nameMap = new Map(this.hostNameMap());
                    const missingIds = top10.map(([id]) => id).filter((id) => !nameMap.has(id));

                    if (missingIds.length === 0) {
                        return of(this.formatTopHosts(top10, nameMap));
                    }

                    return this.hostsService
                        .getAll({
                            filters: { hostLocationId: { _in: missingIds } },
                            fields: ['hostLocationId', 'hostLocationName'],
                        })
                        .pipe(
                            map((res) => {
                                res.data.forEach((h) => {
                                    if (h.hostLocationId) {
                                        nameMap.set(
                                            h.hostLocationId,
                                            h.hostLocationName || h.hostLocationId,
                                        );
                                    }
                                });
                                return this.formatTopHosts(top10, nameMap);
                            }),
                        );
                }),
                withLoading(this.isTopHostsLoading),
                catchError((error: unknown) => {
                    Logger.error('HostsComponent', 'Error loading top hosts:', error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (topHosts) => {
                    this.topHostsData.set(topHosts);
                },
            });
    }

    /**
     * Formats the raw license counts into the UI-bound TopHostEntry model.
     *
     * @private
     * @param {Array<[string, number]>} top10 - Array of Host ID and count tuples.
     * @param {Map<string, string>} nameMap - Lookup map for resolving host names.
     * @returns {TopHostEntry[]} Mapped ranking entries for display.
     */
    private formatTopHosts(
        top10: [string, number][],
        nameMap: Map<string, string>,
    ): TopHostEntry[] {
        return top10.map(([id, count], i) => ({
            rank: i + 1,
            name: nameMap.get(id) ?? id,
            licenses: count,
        }));
    }

    /**
     * Constructs a date range filter object for the createdAt field.
     *
     * @private
     * @param {string} start - ISO start date string.
     * @param {string} end - ISO end date string.
     * @returns {Record<string, FilterOperator>} A filter object for buildQueryParams.
     */
    private buildDateFilters(start: string, end: string): Record<string, FilterOperator> {
        return { createdAt: { _gte: start, _lte: end } };
    }

    /**
     * Maps the host data to a format that can be displayed in the table.
     *
     * @param {ApiHostLocation[]} data - Array of raw host objects from the API.
     * @param {Map<string, string>} [userMap] - Optional map of User ID to formatted Full Name.
     * @param {Map<number, string>} [timezoneMap] - Optional map of Timezone ID to display name.
     * @param {Map<string, number>} [licenseCountMap] - Optional map of Host ID to number of associated licenses.
     * @returns {UiHostTable[]} Array of UI-consumable host records.
     *
     * @example
     * const tableData = this.mappedHostsData(hosts, userNames, timezones, counts);
     */
    public mappedHostsData(
        data: ApiHostLocation[],
        userMap?: Map<string, string>,
        timezoneMap?: Map<number, string>,
        licenseCountMap?: Map<string, number>,
    ): UiHostTable[] {
        return data.map((host) => {
            const resolvedName = userMap?.get(host.ownerId);
            const resolvedTimezone = timezoneMap?.get(host.timezoneId) || 'Unknown';
            const calculatedTotalLicenses = licenseCountMap?.get(host.hostLocationId) || 0;

            return {
                id: host.hostLocationId ?? '',
                hostName: host.hostLocationName ?? '',
                dealerName: resolvedName || 'none for now',
                dealerId: host.ownerId,
                address: host.addressLine1 ?? '',
                city: host?.city ?? '',
                state: host?.state ?? '',
                postalCode: host.zip ?? '',
                timezone: resolvedTimezone,
                programmaticId: 'none for now',
                totalLicenses: calculatedTotalLicenses,
            };
        });
    }

    /**
     * Reference to shared truncateText utility for use in templates.
     */
    public truncateText = truncateText;
}
